import { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { paths } from '../lib/paths';
import { ThemeToggle } from './ThemeToggle';
import { ScorekeeperLogo } from './ScorekeeperLogo';

const HOME_ANCHORS = [
  { href: '/#platform', label: 'Platform' },
  { href: '/#game-day', label: 'Game Day' },
  { href: '/#intelligence', label: 'Intelligence' },
  { href: '/#experience', label: 'Experience' },
  { href: '/#pricing', label: 'Pricing' },
] as const;

/**
 * Marketing chrome - the front door. Quiet light bar, mono wordmark, pill nav.
 *
 * Home route renders full-bleed (its sections supply their own gutters); all
 * other marketing routes are wrapped in `.m-page-wrap` for a centered max-width.
 */
export function MarketingShell() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === paths.marketingHome;
  const demoPath = `${paths.contact}?intent=book_demo`;

  return (
    <div className="marketing-shell">
      <header className="marketing-shell__top">
        <div className="marketing-shell__top-inner">
          <Link to={paths.marketingHome} className="marketing-shell__brand" aria-label="Home">
            <ScorekeeperLogo className="marketing-shell__brand-logo" />
          </Link>

          <nav className="marketing-shell__nav" aria-label="Primary">
            {HOME_ANCHORS.map((item) => (
              <a key={item.href} href={item.href} className="marketing-shell__nav-item">
                {item.label}
              </a>
            ))}
            <Link to={demoPath} className="marketing-shell__nav-item">Demo</Link>
          </nav>

          <div className="marketing-shell__actions">
            <ThemeToggle />
            <Link to={paths.adminHome} className="btn btn--primary btn--sm marketing-shell__login-link">
              League Login
            </Link>
            <button
              type="button"
              className="marketing-shell__menu-btn"
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="marketing-shell__sheet" role="menu">
            {HOME_ANCHORS.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                className="marketing-shell__sheet-item"
              >
                {item.label}
              </a>
            ))}
            <Link
              to={demoPath}
              onClick={() => setOpen(false)}
              className="marketing-shell__sheet-item"
            >
              Demo
            </Link>
            <Link
              to={paths.adminHome}
              onClick={() => setOpen(false)}
              className="marketing-shell__sheet-item is-cta"
            >
              League Login
            </Link>
          </div>
        )}
      </header>

      <main className="marketing-shell__main">
        {isHome ? (
          <Outlet />
        ) : (
          <div className="m-page-wrap">
            <Outlet />
          </div>
        )}
      </main>

      <footer className="marketing-shell__footer">
        <div className="m-os-footer">
          <span className="m-os-footer__brand-line">
            <ScorekeeperLogo className="marketing-shell__footer-logo" />
            <span>Intelligent league management for roller hockey</span>
          </span>
          <span className="m-os-footer__links">
            <a href={paths.about}>About</a>
            <a href={paths.contact}>Contact</a>
            <a href={paths.privacy}>Privacy</a>
            <a href={paths.terms}>Terms</a>
            <span>(c) {new Date().getFullYear()}</span>
            <a href={paths.companyHome} className="m-os-footer__company-link">
              Scorekeeper Company Login
            </a>
          </span>
        </div>
      </footer>
    </div>
  );
}
